type HistoryMessage = {
  role?: string;
  content?: unknown;
  position?: unknown;
};

function textContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  return content
    .filter((item: any) => item && item.type === 'text')
    .map((item: any) => typeof item.text === 'string' ? item.text : '')
    .join('');
}

function comparable(text: string): string {
  return text.normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, '');
}

function hasRenderableContent(message: HistoryMessage): boolean {
  if (textContent(message.content).trim()) return true;
  return Array.isArray(message.content)
    && message.content.some((item: any) => item && item.type !== 'text');
}

function hasAuthoritativeMetadata(message: HistoryMessage): boolean {
  if (Array.isArray(message.content)
      && message.content.some((item: any) => item && item.type !== 'text')) {
    return true;
  }
  return Object.keys(message).some(
    key => !['role', 'content', 'position', 'chat_id'].includes(key),
  );
}

function findCurrentUserIndex(
  live: HistoryMessage[],
  persisted: HistoryMessage[],
  liveIndex: number,
): number {
  const liveUser = live[liveIndex];
  const expectedText = comparable(textContent(liveUser.content));
  const positioned = persisted
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => (
      message?.role === 'user'
      && liveUser.position !== undefined
      && message.position !== undefined
      && message.position === liveUser.position
      && comparable(textContent(message.content)) === expectedText
    ));
  if (positioned.length) return positioned[positioned.length - 1].index;

  // Legacy histories can lack position. Match the same occurrence instead of
  // the first equal "continue"/"ok" in a long chat.
  const liveOccurrence = live.slice(0, liveIndex + 1).filter(
    message => message?.role === 'user'
      && comparable(textContent(message.content)) === expectedText,
  ).length;
  const persistedMatches = persisted
    .map((message, index) => ({ message, index }))
    .filter(({ message }) => message?.role === 'user'
      && comparable(textContent(message.content)) === expectedText);
  return persistedMatches[liveOccurrence - 1]?.index ?? -1;
}

function mergeContent(live: unknown, persisted: unknown): unknown {
  if (!Array.isArray(persisted)) return live;
  const liveItems = Array.isArray(live)
    ? live
    : (typeof live === 'string' && live ? [{ type: 'text', text: live }] : []);
  const liveTexts = liveItems.filter((item: any) => item?.type === 'text');
  const liveNonText = liveItems.filter((item: any) => item?.type !== 'text');
  let textIndex = 0;
  const merged: any[] = [];
  for (const item of persisted) {
    if ((item as any)?.type === 'text') {
      if (textIndex < liveTexts.length) merged.push(liveTexts[textIndex++]);
    } else {
      merged.push(item);
    }
  }
  merged.push(...liveTexts.slice(textIndex));
  for (const item of liveNonText) {
    if (!merged.some(existing => JSON.stringify(existing) === JSON.stringify(item))) {
      merged.push(item);
    }
  }
  return merged;
}

function reconcileCurrentTurn<T extends HistoryMessage>(
  live: T[],
  persisted: HistoryMessage[],
  liveUserIndex: number,
  persistedUserIndex: number,
): T[] {
  const persistedTail = persisted.slice(persistedUserIndex + 1);
  if (!persistedTail.length || !persistedTail.some(hasAuthoritativeMetadata)) {
    return live;
  }
  const liveTail = live.slice(liveUserIndex + 1);
  const usedLive = new Set<number>();
  const mergedTail: HistoryMessage[] = persistedTail.map((persistedMessage) => {
    if (persistedMessage?.role !== 'assistant') return persistedMessage;
    let liveIndex = liveTail.findIndex((message, index) => (
      !usedLive.has(index)
      && message?.role === 'assistant'
      && message.position !== undefined
      && persistedMessage.position !== undefined
      && message.position === persistedMessage.position
    ));
    if (liveIndex < 0) {
      liveIndex = liveTail.findIndex(
        (message, index) => !usedLive.has(index) && message?.role === 'assistant',
      );
    }
    if (liveIndex < 0) return persistedMessage;
    usedLive.add(liveIndex);
    const liveMessage = liveTail[liveIndex];
    return {
      ...liveMessage,
      ...persistedMessage,
      content: mergeContent(liveMessage.content, persistedMessage.content),
    };
  });
  liveTail.forEach((message, index) => {
    if (!usedLive.has(index)) mergedTail.push(message);
  });
  const mergedUser = {
    ...live[liveUserIndex],
    ...persisted[persistedUserIndex],
  } as T;
  return [
    ...persisted.slice(0, persistedUserIndex),
    mergedUser,
    ...mergedTail,
  ] as T[];
}

export function persistedHistoryContainsStreamedAnswer(
  persisted: HistoryMessage[],
  streamedText: string,
): boolean {
  const expected = comparable(streamedText);
  if (!expected) return true;
  const persistedAssistantText = persisted
    .filter(message => message?.role === 'assistant')
    .map(message => textContent(message.content))
    .join('');
  return comparable(persistedAssistantText).includes(expected);
}

/**
 * Mongo normally wins because it has the authoritative tool/content metadata.
 * A resumed stream is the exception: if Mongo does not contain text the user
 * has already received, replacing local state would visibly erase the answer.
 */
export function chooseBackgroundHistory<T extends HistoryMessage>(
  live: T[],
  persisted: HistoryMessage[],
  streamedText: string,
): T[] {
  let liveUserIndex = -1;
  for (let index = live.length - 1; index >= 0; index--) {
    if (live[index]?.role === 'user') {
      liveUserIndex = index;
      break;
    }
  }
  const latestLiveUser = live[liveUserIndex];
  let relevantPersisted = persisted;
  let persistedUserIndex = -1;
  if (latestLiveUser) {
    persistedUserIndex = findCurrentUserIndex(live, persisted, liveUserIndex);
    if (persistedUserIndex < 0) return live;
    relevantPersisted = persisted.slice(persistedUserIndex + 1);
  }
  if (!persistedHistoryContainsStreamedAnswer(relevantPersisted, streamedText)) {
    if (!latestLiveUser) return live;
    return reconcileCurrentTurn(live, persisted, liveUserIndex, persistedUserIndex);
  }
  if (!streamedText && latestLiveUser) {
    const liveTail = live.slice(liveUserIndex + 1);
    const persistedTail = persisted.slice(persistedUserIndex + 1);
    if (liveTail.some(hasRenderableContent) && !persistedTail.some(hasRenderableContent)) {
      return live;
    }
  }
  return persisted as T[];
}
